'use client';

import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import useSelectedTransport from '@/app/components/hotel-detail/hooks/useSelectedTransport';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { isSplitPaymentsEligible } from '@/app/components/search/components/DeferredPayments/splitPayments.ts';
import { Icon } from '@/app/common/icons/Icon';
import { getCheckedBaggageText, getLuggageTranslation } from '../common/luggageTranslations';
import { MealPlanDetailsDeferredPayments, DetailInfoRow, DetailsInfo } from './RoomMealPlanInformation';

export function RoomMealPlanAndLuggageInformation(
  props: { mealPlan: MealPlanType; hideAtol?: boolean; iconSize?: number } & React.HTMLAttributes<HTMLDivElement>
) {
  const { mealPlan, hideAtol = false, iconSize = 16, ...rest } = props;
  const { t } = useTranslation();
  const initialParams = useInitialParams();
  const urlSearchParams = useUrlSearchParams();
  const { data: selectedTransport } = useSelectedTransport();
  const { adults, children } = useUrlSearchParams();
  const { localization } = usePageWrapperData();

  // No branded fare in case of luggage per group
  const { selectedBrandedFare } = useSelectedMealPlan();
  const transport = selectedTransport?.transports[0];
  const cabinBagIncluded =
    !!selectedBrandedFare?.cabinBaggage ||
    (!!transport?.outbound.handBaggageIncluded && !!transport?.inbound.handBaggageIncluded);
  const numberOfBaggagePiecesIncluded = transport?.numberOfBaggagePiecesIncluded ?? 0;
  const showGroupLuggage = numberOfBaggagePiecesIncluded > 0;
  const checkedBagFromProvider =
    !!selectedBrandedFare?.holdBaggage || (!!transport?.outbound.includeBaggage && !!transport?.inbound.includeBaggage);
  const checkedBagIncluded = showGroupLuggage || checkedBagFromProvider;

  const numberOfLuggage = (adults ?? 0) + (children ?? 0);

  const showDeferredPayments = !!mealPlan?.deferredPaymentDeposit;
  const showSplitPayments = isSplitPaymentsEligible({
    localization,
    totalPrice: mealPlan?.finalTotalPrice,
    isHO: urlSearchParams.accommodationOnly,
  });
  const showPaymentInfo = showDeferredPayments || showSplitPayments;
  const showAtol = !hideAtol && initialParams.atol && !urlSearchParams.accommodationOnly;

  return (
    <DetailsInfo {...rest}>
      <DetailInfoRow>
        <Icon category="Semantics" name="icon-check" size={iconSize} color="iconPrimary" alt="small luggage" />
        <Text variant="BodySmall">{getLuggageTranslation('hand', numberOfLuggage, t)}</Text>
      </DetailInfoRow>
      {cabinBagIncluded && (
        <DetailInfoRow>
          <Icon category="Semantics" name="icon-check" size={iconSize} color="iconPrimary" alt="cabin luggage" />
          <Text variant="BodySmall">{getLuggageTranslation('cabin', numberOfLuggage, t)}</Text>
        </DetailInfoRow>
      )}

      {checkedBagIncluded && (
        <DetailInfoRow>
          <Icon category="Semantics" name="icon-check" size={iconSize} color="iconPrimary" alt="checked luggage" />
          <Text variant="BodySmall">
            {getCheckedBaggageText({ showGroupLuggage, numberOfBaggagePiecesIncluded, numberOfLuggage, t })}
          </Text>
        </DetailInfoRow>
      )}
      {mealPlan?.backToTravel && (
        <DetailInfoRow>
          <Icon
            category="Semantics"
            name="icon-check"
            size={iconSize}
            color="iconPrimary"
            alt="Travel Extra Pack included"
          />
          <Text variant="BodySmall">{t('vacation.select.summary.tep_included')}</Text>
        </DetailInfoRow>
      )}
      {showPaymentInfo && (
        <DetailInfoRow>
          <MealPlanDetailsDeferredPayments mealPlan={mealPlan} iconSize={iconSize} />
        </DetailInfoRow>
      )}
      {showAtol && (
        <DetailInfoRow>
          <Icon
            category="Semantics"
            name="icon-check"
            size={iconSize}
            color="iconPrimary"
            alt={urlSearchParams.isTrain ? 'Abtot Protected included' : 'Atol Protected included'}
          />
          <Text variant="BodySmall">
            {t(urlSearchParams.isTrain ? 'vacation.select.abtot.protected' : 'vacation.select.atol.protected')}
          </Text>
        </DetailInfoRow>
      )}
    </DetailsInfo>
  );
}

export { MealPlanDetailsDeferredPayments };
